export interface AssignmentSubjectGroup<T> {
  readonly subjectId: string;
  readonly items: readonly T[];
}

export interface AssignmentClassroomGroup<T> {
  readonly classroomId: string;
  readonly subjectGroups: readonly AssignmentSubjectGroup<T>[];
}

export interface GroupAssignmentsOptions<T> {
  readonly classroomIds: (item: T) => readonly string[];
  readonly subjectId: (item: T) => string;
  readonly subjectSortKey: (subjectId: string) => number;
  readonly dueTimestamp: (item: T) => number;
}

/* Groupe classe -> matière -> devoirs, tri par due_at croissant dans chaque
   sous-groupe. Un devoir multi-classe apparaît dans chaque groupe classe
   concerné (voir consigne Ismaël). L'ordre des groupes classe suit leur
   première apparition dans `items` ; l'appelant, qui seul connaît les noms
   de classe, peut réordonner le résultat. */
export function groupAssignmentsByClassroomAndSubject<T>(
  items: readonly T[],
  options: GroupAssignmentsOptions<T>,
): readonly AssignmentClassroomGroup<T>[] {
  const byClassroom = new Map<string, T[]>();
  for (const item of items) {
    for (const classroomId of options.classroomIds(item)) {
      const bucket = byClassroom.get(classroomId);
      if (bucket) bucket.push(item);
      else byClassroom.set(classroomId, [item]);
    }
  }

  const result: AssignmentClassroomGroup<T>[] = [];
  for (const [classroomId, classroomItems] of byClassroom) {
    const bySubject = new Map<string, T[]>();
    for (const item of classroomItems) {
      const subjectId = options.subjectId(item);
      const bucket = bySubject.get(subjectId);
      if (bucket) bucket.push(item);
      else bySubject.set(subjectId, [item]);
    }

    const subjectGroups = [...bySubject.entries()]
      .sort(([a], [b]) => options.subjectSortKey(a) - options.subjectSortKey(b))
      .map(([subjectId, subjectItems]) => ({
        subjectId,
        items: [...subjectItems].sort((a, b) => options.dueTimestamp(a) - options.dueTimestamp(b)),
      }));

    result.push({ classroomId, subjectGroups });
  }
  return result;
}
